import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { ERAS } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Photo } from "@/components/Photo";
import { Lightbox } from "@/components/Lightbox";
import { Eyebrow, Reveal } from "@/components/Motion";
import { RollingYear } from "@/components/RollingYear";

const COPY = {
  silent: { title: "Roots on the Highveld", line: "Huguenot names, Highveld farms, ox wagons and the gold rush on the Witwatersrand, held still in silver and grain.", bg: "#121212", text: "#EDEDED", font: "font-display" },
  sepia: { title: "From the farm to the mine", line: "Drought, war and depression. Families leave the land, sons go underground, and new mining towns rise on the West Rand.", bg: "#2A1F14", text: "#F3E3C3", font: "font-display italic" },
  faded: { title: "Gold-mining town", line: "Carletonville. A shaft contract, a rescue underground, and a young nurse from Franschhoek.", bg: "#2C2A22", text: "#F4E9D2", font: "font-accent" },
  analog: { title: "Across the Atlantic", line: "A hard decision, and a family crossing to Houston, Texas: some went ahead, the others followed.", bg: "#14232B", text: "#F6EFE0", font: "font-display" },
  digital: { title: "Scanned and saved", line: "The old prints come out of the shoebox on both sides of the ocean: every portrait and carte de visite, kept.", bg: "#0F2230", text: "#F0F4F2", font: "font-display" },
  today: { title: "Two countries, one tree", line: "South African roots, American branches, and the next generation carrying the family forward.", bg: "#0F2A1D", text: "#F6EFE0", font: "font-display" },
};

const FRAME = {
  silent: "", sepia: "p-2 pb-8 bg-[#F3E3C3] rounded-sm rotate-[-1.5deg]", faded: "rounded-[28px] overflow-hidden",
  analog: "", digital: "glass p-2 rounded-2xl", today: "",
};

const EraSection = ({ era, photos, onOpen, onActive }) => {
  const c = COPY[era.id];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  useMotionValueEvent(scrollYProgress, "change", (v) => { if (v > 0.3 && v < 0.7) onActive(era.id); });
  const [lead, ...rest] = photos;
  const grid = era.id === "digital" ? rest : rest.length >= 3 ? rest.slice(0, Math.min(6, Math.floor(rest.length / 3) * 3)) : rest;
  return (
    <section ref={ref} className="relative py-28 md:py-40 transition-colors duration-1000" style={{ background: c.bg, color: c.text }} data-testid={`era-section-${era.id}`}>
      {era.id === "silent" && <><div className="scratches flicker opacity-60" /><div className="vignette" /></>}
      {era.id === "analog" && <motion.div className="scanlines" initial={{ opacity: 0.6 }} whileInView={{ opacity: 0.08 }} transition={{ duration: 2.5 }} viewport={{ once: true, amount: 0.1 }} />}
      {era.id === "today" && <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 80% 20%, rgba(201,162,75,.25), transparent 60%)" }} />}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 md:pl-28">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.4fr] items-start">
          <Reveal>
            <Eyebrow>{era.label} · {era.range[0]}–{era.range[1]}</Eyebrow>
            <h2 className={`mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tight ${c.font}`}>{c.title}</h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed opacity-85 max-w-md">{c.line}</p>
            <p className="mt-8 text-xs font-mono-x tracking-[0.2em] opacity-75">{photos.length} PHOTOGRAPHS · FROM THE ARCHIVE</p>
            {lead?.story && <p className="mt-6 font-accent italic text-lg leading-relaxed opacity-90 max-w-md border-l border-[#C9A24B]/60 pl-4" data-testid={`era-story-${era.id}`}>{lead.story}</p>}
          </Reveal>
          {lead && (
            <motion.div style={{ y }}>
              <div className={FRAME[era.id]}>
                <Photo src={lead.url} era={era.id} ratio="16/10" onClick={() => onOpen(lead)} testid={`era-photo-${era.id}-0`} alt={lead.title} />
              </div>
            </motion.div>
          )}
        </div>
        <div className={`mt-14 grid gap-5 grid-cols-1 sm:grid-cols-2 ${era.id === "digital" ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          {grid.map((ph, i) => (
            <Reveal key={ph.id} delay={(i % 4) * 0.08}>
              <div className={FRAME[era.id]}>
                <Photo src={ph.url} era={era.id} ratio={era.id === "digital" ? "4/5" : "16/10"} onClick={() => onOpen(ph)} testid={`era-photo-${era.id}-${i + 1}`} alt={ph.title} />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const EraJourney = () => {
  const { site } = useSite() || {};
  const ref = useRef(null);
  const [active, setActive] = useState("silent");
  const [box, setBox] = useState({ items: [], i: null });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end center"] });
  const [year, setYear] = useState(1890);
  useMotionValueEvent(scrollYProgress, "change", (v) => setYear(1890 + Math.round(Math.max(0, Math.min(1, v)) * 136)));
  const trunk = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const eras = site?.eras || {};
  const all = ERAS.flatMap((e) => eras[e.id] || []);
  const open = (ph) => setBox({ items: all, i: Math.max(0, all.findIndex((x) => x.id === ph.id)) });
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const ob = new IntersectionObserver(([en]) => setInView(en.isIntersecting), { threshold: 0 });
    if (ref.current) ob.observe(ref.current);
    return () => ob.disconnect();
  }, []);
  return (
    <div id="journey" ref={ref} className="relative" data-testid="era-journey">
      <AnimatePresence>
        {inView && (
          <motion.aside className="hidden md:flex fixed left-6 top-1/2 -translate-y-1/2 z-40 flex-col items-center gap-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="era-side-rail">
            <RollingYear year={year} className="text-xl text-[#E4C87F]" testid="side-year-indicator" />
            <div className="relative h-[46vh] w-px bg-[#C9A24B]/25">
              <motion.div className="absolute top-0 left-0 w-px bg-gradient-to-b from-[#8A6D2C] to-[#E4C87F]" style={{ height: trunk }} />
            </div>
            <p className="font-mono-x text-[11px] tracking-[0.2em] text-[#C9A24B] [writing-mode:vertical-rl] rotate-180">{ERAS.find((e) => e.id === active)?.label}</p>
          </motion.aside>
        )}
      </AnimatePresence>
      <div className="md:hidden sticky top-[72px] z-30 bg-[#0B1D14]/85 backdrop-blur border-b border-[#C9A24B]/20 px-6 py-2 flex items-center justify-between" data-testid="era-mobile-indicator">
        <span className="font-mono-x text-[11px] tracking-[0.2em] text-[#C9A24B]">{ERAS.find((e) => e.id === active)?.label}</span>
        <RollingYear year={year} className="text-lg text-[#E4C87F]" />
      </div>
      <AnimatePresence>
        <motion.div key={active} className="pointer-events-none fixed inset-0 z-[45]" initial={{ opacity: 0.55 }} animate={{ opacity: 0 }} transition={{ duration: 1.2 }}
          style={{ background: "radial-gradient(circle at 20% 30%, rgba(255,170,60,.45), rgba(201,90,30,.15) 40%, transparent 70%)" }} />
      </AnimatePresence>
      {ERAS.map((e) => <EraSection key={e.id} era={e} photos={eras[e.id] || []} onOpen={open} onActive={setActive} />)}
      <Lightbox items={box.items} index={box.i} onClose={() => setBox({ items: [], i: null })} onIndex={(i) => setBox((b) => ({ ...b, i }))} />
    </div>
  );
};
